import { Injectable, inject } from '@angular/core';
import { SESSION_STORAGE } from 'ngx-webstorage-service';
import { Auth, user } from '@angular/fire/auth';
import {
  User,
  signInAnonymously,
  signInWithCustomToken,
} from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';
import { getTime } from 'date-fns';
import { AppHttpClientService } from '../shared/app-http-client.service';
import { AppRouterService } from '../shared/router-service.service';
import { UserType } from '../room/model';
import { LegalAcceptation, LegalChecked } from './login.model';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly auth: Auth = inject(Auth);
  private readonly httpClient = inject(AppHttpClientService);
  private readonly appRouter = inject(AppRouterService);
  private readonly storage = inject(SESSION_STORAGE);

  user$ = user(this.auth);
  private fbUser!: User;

  get user(): User | null {
    return this.fbUser || null;
  }

  constructor() {
    user(this.auth).subscribe((usr) => {
      if (usr) {
        this.fbUser = usr;
      }
    });
  }

  private readonly STORAGE_KEY = 'pweb-user';
  private readonly AUCTION_STORAGE_KEY = 'pweb-auction';

  attachAuctionIdentifier(auctionId: string, auctionName: string) {
    this.storage.set(this.AUCTION_STORAGE_KEY, { auctionId, auctionName });
  }

  async checkTerms(code: string): Promise<LegalAcceptation> {
    const userTerms = await this.httpClient.post<LegalAcceptation>(
      `auth/terms`,
      { code }
    );

    return userTerms || {} as LegalAcceptation;
  }

  login(code: string, legal: LegalChecked) {
    return new Promise((resolve, reject) => {
      this.httpClient
        .post<string>(`auth`, {
          code,
          legal: { terms: legal.terms, privacy: legal.privacy },
        })
        .then((res: any) => {
          if (res?.token) {
            this.loginWithFb(res.token)
              .then(() => resolve(res))
              .catch((err: Error) => reject(err));
          } else {
            reject(new Error('Invalid response format'));
          }
        })
        .catch((err: Error) => reject(err));
    });
  }

  anonymous(): Promise<boolean> {
    return new Promise((res, _err) => {
      signInAnonymously(this.auth)
        .then(() => {
          this.storage.set(this.STORAGE_KEY, {
            username: 'Invitado',
            uid: `INV${getTime(new Date())}`,
          });
          res(true);
        })
        .catch((error: Error) => {
          _err(error);
        });
    });
  }

  sendUserRequest(userData: any) {
    return this.httpClient.post(`auth/access`, userData);
  }

  remindRequest(requestId: any) {
    return this.httpClient.post('auth/access/resend', { requestId });
  }

  requestAccessCode(phone: string) {
    return this.httpClient.post('auth/remember-access-code', { phone });
  }

  loginWithFb(token: any) {
    return new Promise((resolve, reject) => {
      const userData = jwtDecode<any>(token);
      if (!userData?.fbt) {
        reject(new Error('Invalid token: missing Firebase custom token'));
        return;
      }

      signInWithCustomToken(this.auth, userData.fbt)
        .then(() => {
          this.storage.set(this.STORAGE_KEY, token);
          resolve(token);
        })
        .catch((err: Error) => {
          reject(err);
        });
    });
  }

  clear(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.storage.clear();
      this.auth
        .signOut()
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  clearAuction() {
    this.storage.remove(this.AUCTION_STORAGE_KEY);
  }

  logout(): void {
    this.clear().then(() => this.appRouter.goToLogin());
  }

  refresh(token: any) {
    if (!token) {
      return false;
    }

    this.storage.set(this.STORAGE_KEY, token);
    return true;
  }

  isAnonymous() {
    return !this.getToken() || this.getToken().username === 'Invitado';
  }

  async isValidUserSession() {
    return !this.isAnonymous() && (await this.isNotExpired());
  }

  getSessionInfo() {
    return new Promise((res, rej) => {
      if (this.isAnonymous()) {
        res({
          username: this.getUser().username,
          userType: UserType.VIRTUAL,
          isUserActive: false,
        });
        return;
      }

      this.httpClient
        .get('auth')
        .then((usr) => res(usr))
        .catch((err: Error) => rej(err));
    });
  }

  isNotExpired() {
    const self = this;
    return new Promise<boolean>((res) => {
      if (!this.fbUser || !self.getUser()) {
        res(false);
        return;
      }

      res(true);
    });
  }

  // El método getUser obtiene el usuario actual desde el token almacenado.
  getUser() {
    const token = this.storage.get(this.STORAGE_KEY);
    if (!token) {
      return null;
    }

    if (token.username) {
      return token;
    }

    if (token) {
      return jwtDecode<any>(this.storage.get(this.STORAGE_KEY));
    }

    return null;
  }

  getToken() {
    return this.storage.get(this.STORAGE_KEY) || null;
  }

  getAuction() {
    return this.storage.get(this.AUCTION_STORAGE_KEY) || null;
  }
}
